import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import SectionHeading from "@/components/SectionHeading";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    num: "01",
    title: "Knowledge before action",
    copy: "Most people fight their credit report blind. We turn on the lights first — because a decision made with understanding is worth ten made in panic.",
  },
  {
    num: "02",
    title: "Your report, decoded",
    copy: "Every line item tells a story, and every story has levers. We teach you to read your report the way analysts and underwriters do.",
  },
  {
    num: "03",
    title: "Discipline compounds",
    copy: "There are no overnight miracles — only boring, brilliant consistency. Small moves, sequenced correctly, become hundred-point outcomes.",
  },
  {
    num: "04",
    title: "Elevation is a practice",
    copy: "A stronger score is not the finish line. It's the foundation for lower rates, real funding, and a financial life built on your terms.",
  },
];

const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="manifesto" ref={ref} className="relative py-28 lg:py-40 bg-ink-surface overflow-hidden" data-testid="manifesto-section">
      <motion.span
        style={{ y: bgY }}
        className="pointer-events-none select-none absolute -top-10 right-0 font-serif italic text-[22rem] leading-none text-stroke-gold opacity-40 hidden lg:block"
        aria-hidden="true"
      >
        Apex
      </motion.span>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading
          overline="The Manifesto"
          title={<>Four principles we<br />never <span className="italic text-gold">negotiate.</span></>}
          testId="manifesto-heading"
        />

        <div className="mt-20 lg:mt-28">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: EASE }}
              className="group grid grid-cols-12 gap-6 py-10 lg:py-12 border-t border-white/10 items-baseline hover:bg-white/[0.02] transition-colors duration-500"
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <span className="col-span-3 lg:col-span-2 font-mono text-sm text-gold">{c.num}</span>
              <h3 className="col-span-9 lg:col-span-4 font-serif text-2xl lg:text-4xl text-white tracking-tight group-hover:text-gold transition-colors duration-500">
                {c.title}
              </h3>
              <p className="col-span-9 col-start-4 lg:col-span-5 lg:col-start-8 text-sm lg:text-base text-neutral-400 font-light leading-relaxed">
                {c.copy}
              </p>
            </motion.div>
          ))}
          <div className="border-t border-white/10" />
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
